// const BASE_URL = 'http://localhost:5030/api/v1';
const BASE_URL = 'https://sokohuru.africatech.co.ke/api/v1';


// Configure default options for all API calls
const defaultOptions = {
  credentials: 'include', // This ensures cookies/tokens are sent with requests
  headers: {
    'Content-Type': 'application/json',
  },
};

// Generic API call function
const apiCall = async (endpoint, options = {}) => {
  const url = `${BASE_URL}${endpoint}`;
  const config = {
    ...defaultOptions,
    ...options,
    headers: {
      ...defaultOptions.headers,
      ...options.headers,
    },
  };

  try {
    console.log(`📤 [API] Calling ${endpoint}...`);
    const fetchStart = performance.now();
    
    const response = await fetch(url, config);
    const fetchEnd = performance.now();
    console.log(`📥 [API] ${endpoint} fetch completed in ${(fetchEnd - fetchStart).toFixed(2)}ms`);
    
    // Handle different response types
    const contentType = response.headers.get('content-type');
    let data;
    
    if (contentType && contentType.includes('application/json')) {
      console.log(`🔄 [API] Parsing JSON for ${endpoint}...`);
      const parseStart = performance.now();
      data = await response.json();
      const parseEnd = performance.now();
      console.log(`✅ [API] ${endpoint} JSON parsed in ${(parseEnd - parseStart).toFixed(2)}ms`);
      
      // Log data size
      const dataStr = JSON.stringify(data);
      console.log(`📊 [API] ${endpoint} response size: ${(dataStr.length / 1024).toFixed(2)} KB`);
    } else {
      data = await response.text();
    }

    if (!response.ok) {
      // Create a custom error with status code and user-friendly message
      const error = new Error();
      error.status = response.status;
      error.data = data;
      const backendMessage = data?.message || data?.error;
      
      // Set user-friendly error messages based on status code
      switch (response.status) {
        case 401:
          error.message = 'Invalid email or password';
          break;
        case 502:
          error.message = 'System is down. Please contact admin.';
          break;
        case 403:
          error.message = 'Access denied. You do not have permission to perform this action.';
          break;
        case 404:
          error.message = 'The requested resource was not found.';
          break;
        case 422:
          error.message = backendMessage || 'Invalid data provided. Please check your input.';
          break;
        case 500:
          error.message = 'Internal server error. Please try again later.';
          break;
        default:
          error.message = backendMessage || 'An unexpected error occurred. Please try again.';
      }
      
      throw error;
    }

    return {
      data,
      status: response.status,
      ok: response.ok,
    };
  } catch (error) {
    // If it's already our custom error, re-throw it
    if (error.status) {
      throw error;
    }
    
    // Handle network errors or other fetch errors
    console.error(`API call failed for ${endpoint}:`, error);
    
    // Create a custom error for network issues
    const networkError = new Error();
    networkError.status = 0; // Network error
    networkError.message = 'Network error. Please check your internet connection and try again.';
    throw networkError;
  }
};

// Authentication API calls
export const authAPI = {
  // Verify if current token is active
  verifyToken: async () => {
    try {
      const response = await apiCall('/verify');
      return response.status === 200;
    } catch (error) {
      return false;
    }
  },

  // Admin login
  loginAdmin: async (credentials) => {
    return apiCall('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Presenter login
  loginPresenter: async (credentials) => {
    return apiCall('/login', {
      method: 'POST',
      body: JSON.stringify(credentials),
    });
  },

  // Logout (for both admin and presenter)
  logout: async () => {
    return apiCall('/logout', {
      method: 'POST',
    });
  },

  // Get current user info
  getCurrentUser: async () => {
    return apiCall('/verify');
  },
};

// Admin API calls
export const adminAPI = {
  // Items
  getItems: async (page = null, limit = null) => {
    // If pagination params are provided, use them
    if (page !== null && limit !== null) {
      return apiCall(`/get-items?page=${page}&limit=${limit}`);
    }
    // Otherwise, get all items (backward compatibility)
    return apiCall('/get-items');
  },
  
  // Get items count (for pagination)
  getItemsCount: async () => apiCall('/get-items-count'),
  
  createItem: async (itemData) => apiCall('/create-item', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  addItem: async (itemData) => apiCall('/add/item', {
    method: 'POST',
    body: JSON.stringify(itemData),
  }),
  getItem: async (auctionId) => apiCall(`/get/item?id=${auctionId}`),
  updateItem: async (itemData) => apiCall('/update/item', {
    method: 'PUT',
    body: JSON.stringify(itemData),
  }),

  deleteItem: async (itemData) => apiCall('/delete-item', {
    method: 'DELETE',
    body: JSON.stringify(itemData),
  }),

  // Incentives
  getIncentives: async () => apiCall('/admin/incentives'),
  addIncentive: async (incentiveData) => apiCall('/admin/incentive', {
    method: 'POST',
    body: JSON.stringify(incentiveData),
  }),
  updateIncentive: async (incentiveData) => apiCall('/admin/incentive', {
    method: 'PUT',
    body: JSON.stringify(incentiveData),
  }),
  deleteIncentive: async (incentiveData) => apiCall('/admin/incentive', {
    method: 'DELETE',
    body: JSON.stringify(incentiveData),
  }),

  // Radio Shows
  getRadioShows: async () => apiCall('/get-radio-shows'),
  createRadioShow: async (showData) => apiCall('/create-radio-show', {
    method: 'POST',
    body: JSON.stringify(showData),
  }),
  updateRadioShow: async (showData) => apiCall('/update-radio-show', {
    method: 'PUT',
    body: JSON.stringify(showData),
  }),
  deleteRadioShow: async (showData) => apiCall('/delete-radio-show', {
    method: 'DELETE',
    body: JSON.stringify(showData),
  }),
  getRadioShowItems: async (showData) => apiCall('/get-radio-show-items', {
    method: 'GET',
    body: JSON.stringify(showData),
  }),

  // Presenters
  getPresenters: async () => apiCall('/get-presenters'),
  createPresenter: async (presenterData) => apiCall('/create-presenter', {
    method: 'POST',
    body: JSON.stringify(presenterData),
  }),
  updatePresenter: async (presenterData) => apiCall('/update-presenter', {
    method: 'PUT',
    body: JSON.stringify(presenterData),
  }),
  assignUSSDToPresenter: async (presenterId, ussdCodeId) => apiCall('/assign-ussd-to-presenter', {
    method: 'POST',
    body: JSON.stringify({
      presenter_id: presenterId,
      ussd_code_id: ussdCodeId
    }),
  }),
  unassignUSSDFromPresenter: async (presenterId) => apiCall('/unassign-ussd-from-presenter', {
    method: 'DELETE',
    body: JSON.stringify({ presenter_id: presenterId }),
  }),
  deletePresenter: async (presenterData) => apiCall('/delete-presenter', {
    method: 'DELETE',
    body: JSON.stringify(presenterData),
  }),

  // Analytics and Reports
  getDashboardStats: async () => apiCall('/admin/dashboard/stats'),
  getBiddingReport: async (startDate, endDate) => apiCall(`/admin/reports/bidding?start=${startDate}&end=${endDate}`),
  
  // Analytics API
  getComprehensiveDashboard: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/comprehensive-dashboard?${queryString}` : '/analytics/comprehensive-dashboard';
    return apiCall(endpoint);
  },
  
  getCustomerIntelligence: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/customer-intelligence?${queryString}` : '/analytics/customer-intelligence';
    return apiCall(endpoint);
  },
  
  getAuctionPerformance: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/auction-performance?${queryString}` : '/analytics/auction-performance';
    return apiCall(endpoint);
  },
  
  getAllItemsTransactions: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/admin/all-items-transactions?${queryString}` : '/admin/all-items-transactions';
    return apiCall(endpoint);
  },
  
  // Legacy endpoints for backward compatibility
  getDashboardAnalytics: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/dashboard?${queryString}` : '/analytics/dashboard';
    return apiCall(endpoint);
  },
  
  getCustomerInsights: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/customer-insights?${queryString}` : '/analytics/customer-insights';
    return apiCall(endpoint);
  },
  
  getRevenueTrends: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/revenue-trends?${queryString}` : '/analytics/revenue-trends';
    return apiCall(endpoint);
  },
  
  getPerformanceMetrics: async (filters = {}) => {
    const params = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        params.append(key, value);
      }
    });
    const queryString = params.toString();
    const endpoint = queryString ? `/analytics/performance-metrics?${queryString}` : '/analytics/performance-metrics';
    return apiCall(endpoint);
  },
  
  getRealTimeStats: async () => apiCall('/analytics/real-time-stats'),

  // USSD Codes
  getUSSDCodes: async () => apiCall('/get-ussd-codes'),
  createUSSDCode: async (ussdData) => apiCall('/create-ussd-code', {
    method: 'POST',
    body: JSON.stringify(ussdData),
  }),
  updateUSSDCode: async (ussdData) => apiCall('/update-ussd-code', {
    method: 'PUT',
    body: JSON.stringify(ussdData),
  }),
  deleteUSSDCode: async (ussdData) => apiCall('/delete-ussd-code', {
    method: 'DELETE',
    body: JSON.stringify(ussdData),
  }),

  // Shortcode Statistics
  getShortcodeStats: async (endpoint = '/shortcode-stats') => apiCall(endpoint),
  
  // Shortcode Statistics with time filtering
  getShortcodeStatsWithTime: async (startDate = '', endDate = '', startTime = '', endTime = '') => {
    const params = new URLSearchParams();
    
    // If only time is provided, use today's date as default
    const today = new Date().toISOString().split('T')[0];
    const effectiveStartDate = startDate || (startTime || endTime ? today : '');
    const effectiveEndDate = endDate || (startTime || endTime ? today : '');
    
    if (effectiveStartDate) {
      params.append('start_date', effectiveStartDate);
    }
    if (effectiveEndDate) {
      params.append('end_date', effectiveEndDate);
    }
    if (startTime) {
      params.append('start_time', startTime);
    }
    if (endTime) {
      params.append('end_time', endTime);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/shortcode-stats?${queryString}` : '/shortcode-stats';
    return apiCall(endpoint);
  },
  
  // Shortcode Details with Transactions
  getShortcodeDetails: async (shortcode, startDate = '', endDate = '', startTime = '', endTime = '') => {
    const params = new URLSearchParams();
    
    // If only time is provided, use today's date as default
    const today = new Date().toISOString().split('T')[0];
    const effectiveStartDate = startDate || (startTime || endTime ? today : '');
    const effectiveEndDate = endDate || (startTime || endTime ? today : '');
    
    if (effectiveStartDate) {
      params.append('start_date', effectiveStartDate);
    }
    if (effectiveEndDate) {
      params.append('end_date', effectiveEndDate);
    }
    if (startTime) {
      params.append('start_time', startTime);
    }
    if (endTime) {
      params.append('end_time', endTime);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/shortcode-details/${shortcode}?${queryString}` : `/shortcode-details/${shortcode}`;
    return apiCall(endpoint);
  },

  // Auction Statistics
  getAuctionStats: async (status = '', startDate = '', endDate = '', startTime = '', endTime = '') => {
    const params = new URLSearchParams();
    if (status) {
      params.append('status', status);
    }
    
    // If only time is provided, use today's date as default
    const today = new Date().toISOString().split('T')[0];
    const effectiveStartDate = startDate || (startTime || endTime ? today : '');
    const effectiveEndDate = endDate || (startTime || endTime ? today : '');
    
    if (effectiveStartDate) {
      params.append('start_date', effectiveStartDate);
    }
    if (effectiveEndDate) {
      params.append('end_date', effectiveEndDate);
    }
    if (startTime) {
      params.append('start_time', startTime);
    }
    if (endTime) {
      params.append('end_time', endTime);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/auction-stats?${queryString}` : '/auction-stats';
    return apiCall(endpoint);
  },

  // Radio Show Performance with time filtering
  getRadioShowPerformance: async (startDate = '', endDate = '', startTime = '', endTime = '') => {
    const params = new URLSearchParams();
    
    // If only time is provided, use today's date as default
    const today = new Date().toISOString().split('T')[0];
    const effectiveStartDate = startDate || (startTime || endTime ? today : '');
    const effectiveEndDate = endDate || (startTime || endTime ? today : '');
    
    if (effectiveStartDate) {
      params.append('start_date', effectiveStartDate);
    }
    if (effectiveEndDate) {
      params.append('end_date', effectiveEndDate);
    }
    if (startTime) {
      params.append('start_time', startTime);
    }
    if (endTime) {
      params.append('end_time', endTime);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/radio-show-performance?${queryString}` : '/radio-show-performance';
    return apiCall(endpoint);
  },
};

// Presenter API calls
export const presenterAPI = {
  // Items with filtering
  getActiveItems: async (filters = {}) => {
    const params = new URLSearchParams();
    
    // Add filter parameters
    if (filters.filterType) {
      params.append('filter_type', filters.filterType);
    }
    if (filters.startDate) {
      params.append('start_date', filters.startDate);
    }
    if (filters.endDate) {
      params.append('end_date', filters.endDate);
    }
    if (filters.startTime) {
      params.append('start_time', filters.startTime);
    }
    if (filters.endTime) {
      params.append('end_time', filters.endTime);
    }
    
    const queryString = params.toString();
    const endpoint = queryString ? `/get-presenter-items?${queryString}` : '/get-presenter-items';
    return apiCall(endpoint);
  },
  
  getItemDetails: async (itemId) => apiCall(`/presenter/items/${itemId}`),

  // Transactions
  getBidsForItem: async (itemData) => apiCall('/get-item-transactions', {
    method: 'GET',
    body: JSON.stringify(itemData),
  }),

  // Winner Management
  drawWinner: async (itemId) => apiCall(`/presenter/items/${itemId}/draw-winner`, {
    method: 'POST',
  }),
  confirmWinner: async (itemId, winnerId) => apiCall(`/presenter/items/${itemId}/confirm-winner`, {
    method: 'POST',
    body: JSON.stringify({ winnerId }),
  }),
  
  // Close Bid/Auction
  closeBid: async (auctionId) => apiCall('/close-bid', {
    method: 'POST',
    body: JSON.stringify({ auction_id: auctionId }),
  }),

  // Live Updates
  getBiddingStats: async () => apiCall('/presenter/stats'),
};

// Bidding API calls (for live bidding system)
export const biddingAPI = {
  // Place a bid
  placeBid: async (itemId, bidData) => apiCall(`/bidding/items/${itemId}/bid`, {
    method: 'POST',
    body: JSON.stringify(bidData),
  }),

  // Get current bid status for an item
  getItemBidStatus: async (itemId) => apiCall(`/bidding/items/${itemId}/status`),

  // Get live bidding updates
  getLiveBiddingUpdates: async () => apiCall('/bidding/live-updates'),
};

// Utility functions
export const apiUtils = {
  // Handle API errors consistently
  handleError: (error) => {
    // Handle authentication errors
    if (error.status === 401) {
      // Don't handle 401 errors here - let the AuthContext handle them
      // This prevents multiple redirects
      console.error('Authentication error:', error);
      return;
    }
    
    // Show user-friendly error message
    console.error('API Error:', error);
    alert(error.message || 'An unexpected error occurred. Please try again.');
  },

  // Format error messages for display
  formatErrorMessage: (error) => {
    if (typeof error === 'string') return error;
    
    // Use the custom error message if available
    if (error.message) {
      return error.message;
    }
    
    // Fallback to generic message
    return 'An unexpected error occurred. Please try again.';
  },

  // Check if user is authenticated
  isAuthenticated: async () => {
    try {
      return await authAPI.verifyToken();
    } catch (error) {
      return false;
    }
  },
};

// Export base configuration for direct use if needed
export const API_CONFIG = {
  BASE_URL,
  defaultOptions,
};

const apiService = {
  auth: authAPI,
  admin: adminAPI,
  presenter: presenterAPI,
  bidding: biddingAPI,
  utils: apiUtils,
  config: API_CONFIG,
};

export default apiService; 
